import { ResponseResult, TaskStatus } from '@amzn/gbsa-helios-typescript-client';

import { formatDisplayDate } from 'src/v2/shared/domain/formatDisplayDate';
import type {
  OpenTaskRef,
  RecurringFailureItem,
} from 'src/types/starlight/AuditInsights';
import type { AuditInsightsMessageKey } from '../translations';
import { SEPARATOR, streakKey, type MetaFormatters } from './insightRowMeta';

/**
 * Whether a value carries a verdict of its own, so the view can tone-match it.
 *
 * Named by meaning rather than by colour: choosing the token is the view's job, not this
 * module's. Declared once as a const map so the names cannot drift between the type and
 * the code that sets them.
 */
export const DETAIL_TONE = {
  critical: 'critical',
  positive: 'positive',
} as const;

export type DetailTone = (typeof DETAIL_TONE)[keyof typeof DETAIL_TONE];

/** How one recorded answer reads: the wording for it, and the verdict it carries. */
export interface ResultReading {
  label: AuditInsightsMessageKey;
  /** Absent for a result that is neither good nor bad news, which is N/A alone. */
  tone?: DetailTone;
}

/**
 * How each answer reads to an auditor, and whether it carries a verdict worth
 * tone-matching. Keyed off the SDK enum so a value Helios adds is a compile error here
 * rather than a blank cell.
 *
 * Exported because the question table reports the same answers as this modal does, and one
 * of them reading `Pass` in green while the other reads `PASS` in grey would be the tab
 * disagreeing with itself.
 */
const RESULT_READING: Record<ResponseResult, ResultReading> = {
  [ResponseResult.PASS]: { label: 'resultPass', tone: DETAIL_TONE.positive },
  [ResponseResult.FAIL]: { label: 'resultFail', tone: DETAIL_TONE.critical },
  [ResponseResult.NA]: { label: 'resultNa' },
};

// A Map, so a lookup reports a miss. Indexing the Record types as always-present, which
// is untrue of a value off the wire and would hand callers a blank label instead of a miss
// they can handle.
const READING_BY_RESULT = new Map<string, ResultReading>(Object.entries(RESULT_READING));

/**
 * How a recorded result reads, or undefined for one this build does not know.
 *
 * @param result - The result as stored, absent on a question nobody has answered.
 */
export function resultReading(result: string | undefined): ResultReading | undefined {
  return result ? READING_BY_RESULT.get(result) : undefined;
}

/** One recorded fact, as a label and the value beside it. */
export interface DetailField {
  label: string;
  value: string;
  /** Absent when the value states a fact rather than a verdict, which is most of them. */
  tone?: DetailTone;
}

/** A row's recorded facts, ready to render without knowing the model shape. */
export interface InsightDetail {
  title: string;
  /** What the audit asked, so the reader is not working from the title alone. */
  questionText?: string;
  /** Explains where the facts came from, when that is not the audit being read. */
  provenance?: string;
  fields: DetailField[];
  /**
   * Where the reader can go to act on this. Absent for a question, which this tab reports
   * on but does not own a page for.
   */
  viewHref?: string;
}

/** What the caller has to resolve for a task, because the model does not carry it. */
export interface OpenTaskContext {
  /** The status as a reader should see it, spelled the way the task list spells it. */
  statusLabel: string;
  /** The task's own page, so the reader can act rather than only read. */
  viewHref: string;
}

/**
 * The facts recorded against a repeat failure.
 *
 * Describes the most recent **earlier** audit, not this one. This audit's own answer is
 * always a fail — that is why the row exists — so it carries nothing a reader needs, and
 * `priorResults` is what says whether the failure is new or long-standing. The row's own
 * summary line reads the same prior, so opening a row cannot contradict it.
 *
 * @param item - The recurring failure, whose `priorResults` are newest first.
 * @param formatters - Message lookup for the labels and the recurrence phrasing.
 * @param questionText - What the audit asked. The insight names the question but does not
 *   carry its wording, so the caller joins it in from the audit summary by id. Absent when
 *   the summary has no matching question.
 */
export function recurringDetail(
  item: RecurringFailureItem,
  { f }: MetaFormatters,
  questionText?: string,
): InsightDetail {
  const newest = item.priorResults?.[0];
  // Undefined for a result this build does not know, which then reads as unrecorded
  // rather than as a blank cell.
  const recorded = resultReading(newest?.result);
  const absent = f('detailNotRecorded', {});

  return {
    title: titleFor(f('questionIdChip', { id: item.questionId }), item.questionTitle),
    ...(questionText ? { questionText } : {}),
    ...(newest ? { provenance: f('detailPriorAudit', {}) } : {}),
    fields: [
      {
        label: f('detailResult', {}),
        value: recorded ? f(recorded.label, {}) : absent,
        ...(recorded?.tone ? { tone: recorded.tone } : {}),
      },
      {
        label: f('detailAnsweredOn', {}),
        value: newest?.respondedAt ? formatDisplayDate(newest.respondedAt) : absent,
      },
      { label: f('detailAnsweredBy', {}), value: newest?.respondedBy ?? absent },
      {
        label: f('detailRecurrence', {}),
        value: f(streakKey(item.consecutiveFailCount), {}),
      },
    ],
  };
}

/**
 * The facts recorded against an outstanding action.
 *
 * The due date says it is past only when Helios has already moved the task to overdue.
 * Comparing the date against now here would let this view call a task late while its own
 * status still said otherwise.
 *
 * @param task - The open task, whose every fact past the linked question is optional.
 * @param formatters - Message lookup for the labels.
 * @param context - The status label and task page the caller resolved.
 */
export function openTaskDetail(
  task: OpenTaskRef,
  { f }: MetaFormatters,
  { statusLabel, viewHref }: OpenTaskContext,
): InsightDetail {
  const absent = f('detailNotRecorded', {});
  const isOverdue = task.taskStatus === TaskStatus.OVERDUE;

  return {
    title: task.title ?? task.linkedQuestionTitle ?? task.taskId,
    // Ordered as the task's own life is: what it is now, when and by whom it was raised,
    // who owns it, when it is due, and last the question it came from.
    fields: [
      { label: f('detailStatus', {}), value: statusLabel },
      {
        label: f('detailCreated', {}),
        value: task.createdAt ? formatDisplayDate(task.createdAt) : absent,
      },
      { label: f('detailCreatedBy', {}), value: task.createdBy ?? absent },
      { label: f('detailAssignee', {}), value: task.assigneeLogin ?? absent },
      { label: f('detailDue', {}), value: due(task, isOverdue, f, absent) },
      {
        label: f('detailLinkedQuestion', {}),
        value: titleFor(
          f('questionIdChip', { id: task.linkedQuestionId }),
          task.linkedQuestionTitle,
        ),
      },
    ],
    viewHref,
  };
}

/** The due date, marked as passed when the task's own status already says so. */
function due(
  task: OpenTaskRef,
  isOverdue: boolean,
  f: MetaFormatters['f'],
  absent: string,
): string {
  if (!task.dueDate) {
    return absent;
  }

  const date = formatDisplayDate(task.dueDate);
  return isOverdue ? f('detailDuePastDue', { date }) : date;
}

/** The labelled id, plus the name when the model recorded one. */
function titleFor(labelledId: string, name: string | undefined): string {
  return name ? `${labelledId}${SEPARATOR}${name}` : labelledId;
}
